import React, { useCallback, useEffect, useState } from 'react';
import { ReactComponent as PlusIcon } from '../../../assets/images/PlusIcon.svg';
import { ICropBrandAssociation } from '../CropBrandAssociation';
import { themeGet } from '@styled-system/theme-get';
import styled, { useTheme } from 'styled-components';
import { ReactComponent as PullTabOpen } from '../Icons/PullTabOpen.svg';
import { ReactComponent as PullTabClosed } from '../Icons/PullTabClosed.svg';
import { ReactComponent as GreenAvailabilityIcon } from '../../../assets/images/AvailabilityIcons/Green.svg';
import { ReactComponent as YellowAvailabilityIcon } from '../../../assets/images/AvailabilityIcons/Yellow.svg';
import { ReactComponent as RedAvailabilityIcon } from '../../../assets/images/AvailabilityIcons/Red.svg';
import { ReactComponent as InfoIcon } from '../../../assets/images/AvailabilityIcons/Info.svg';
import { Modal } from 'antd';
import { SeriesAvailability } from '../SeriesAvailability';
import { IconButton } from '../../../components/IconButton/IconButton';
import { ReactComponent as SortArrows } from '../../../components/Table/Icons/SortArrows.svg';

export type SeedSortDirection = 'ascending' | 'descending' | 'none';

/** All fields of a series that are needed for this UI. */
export type Series = {
	/** Unique identifier. */
	id: string;
	/** Display friendly name. */
	name: string;
	/** Expects: Green, Yellow, Red. If unknown, assume Red. */
	availability: string;
	relativeMaturity: number;
	/** Whether to show the + on the series. Typically you'll want to set this false when the user has selected the series already. */
	selectable: boolean;
};

export interface IPlanProductListProps<TSeries extends Series = Series>
{
	recommendation: ICropBrandAssociation;
	seriesList: TSeries[];
	planId: string;
	onAddSeries: (series: TSeries) => void;
}

/**
 * The list of selected products from within the product plan editing section. 
 * Dumb UI component. Props should only contain data required for drawing this UI.
 */
export function PlanProductList<TSeries extends Series>(props: IPlanProductListProps<TSeries>)
{
	const { recommendation, seriesList, onAddSeries, } = props;

	const theme = useTheme();

	const [visible, setVisible] = useState<boolean>(true);
	const [showAvailability, setShowAvailability] = useState<boolean>(false);

	const [originalSeriesList, setoriginalSeriesList] = useState<TSeries[]>([...seriesList]);
	const [displayedItems, setDisplayedItems] = useState<TSeries[]>([...seriesList]);
	const [sortState, setSortState] = useState<SeedSortDirection>('none');

	useEffect(() => 
	{
		setoriginalSeriesList([...seriesList]);
		setDisplayedItems([...seriesList]);
		setSortState('none');
	}, [seriesList]);

	const cycleSortState = () => 
	{
		const nextSortState: Record<SeedSortDirection, SeedSortDirection> = {
			'none': 'ascending',
			'ascending': 'descending',
			'descending': 'none'
		};

		const newSortState = nextSortState[sortState];
		setSortState(newSortState);

		switch (newSortState)
		{
			case 'ascending': setDisplayedItems([...displayedItems].sort((a, b) => a.relativeMaturity - b.relativeMaturity)); break;
			case 'descending': setDisplayedItems([...displayedItems].sort((a, b) => b.relativeMaturity - a.relativeMaturity)); break;
			default: setDisplayedItems([...originalSeriesList]);
		}
	};

	return (
		<ProductListContainer className='ProductList' style={{ minWidth: visible ? containerOpenWidth : containerClosedWidth }}>
			<PullTabButton className='PullTabButton'
				onClick={() => setVisible(!visible)}
				style={{ left: visible ? sidebarWidth + containerOpenWidth : sidebarWidth + containerClosedWidth }}
			>
				{visible ? <PullTabOpen /> : <PullTabClosed />}
			</PullTabButton>
			<Modal
				width={225}
				title='Status Legend'
				open={showAvailability}
				okButtonProps={{ style: { display: 'none' } }}
				cancelButtonProps={{ style: { display: 'none' } }}
				onOk={() => setShowAvailability(false)}
				onCancel={() => setShowAvailability(false)}
			>
				<AvailabilityLegend className='AvailabilityLegend'>
					<div style={{ display: 'flex', flexDirection: 'column' }}>
						<div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
							<GreenAvailabilityIcon style={{ fill: 'rgb(160, 160, 160)', position: 'relative', top: -1, marginRight: 2 }} />
							/
							<GreenAvailabilityIcon style={{ fill: theme.colors.availabilityGreen, marginLeft: 2, marginRight: 5, position: 'relative', top: -1 }} />
							Available
						</div>
						<div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
							<YellowAvailabilityIcon style={{ fill: 'rgb(160, 160, 160)', marginRight: 2 }} />
							/
							<YellowAvailabilityIcon style={{ fill: theme.colors.availabilityYellow, marginLeft: 2, marginRight: 5 }} />
							Limited Availability
						</div>
						<div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
							<RedAvailabilityIcon style={{ fill: 'rgb(160, 160, 160)', position: 'relative', top: -1, marginRight: 2 }} />
							/
							<RedAvailabilityIcon style={{ fill: theme.colors.availabilityRed, marginLeft: 2, marginRight: 5, position: 'relative', top: -1 }} />
							Unavailable
						</div>
					</div>
				</AvailabilityLegend>
			</Modal>
			<ProductListContent className='ProductListContent' style={{ visibility: visible ? 'visible' : 'hidden' }}>
				<ProductListHeader className='ProductListHeader' style={{ opacity: visible ? '1' : '0' }}>
					<div className='inner'>
						<div>{recommendation.title} Products</div>
						<InfoIcon className='infoIcon' onClick={() => setShowAvailability(true)} style={{ cursor: 'pointer' }} />
					</div>
					<IconButton
						tooltip={'Seeds in the list below are sorted by Match Strength by default'}
						placement='bottom'
						onClick={cycleSortState}
					>
						<StyledSortArrows sort={sortState}/>
					</IconButton>
				</ProductListHeader>
				{
					!displayedItems.length ?
						<div style={{ 
							flexGrow: 1, 
							display: 'flex', 
							alignItems: 'center', 
							justifyContent: 'center',
							textAlign: 'center',
							padding: '1rem',
						}}>
								There are no seeds available or no seeds available within the chosen filter.
						</div>
						:
						<ProductListSeries className='ProductListSeriesContainer'>
							<div className='ProductListSeriesInner'>
								{displayedItems.map((series) => (
									<SeriesRow className='SeriesContainer' key={series.id + 'FilteredSeriesList'}>
										<SeriesAvailability className='SeriesAvailability'>
											{series.availability == 'Green' ? <GreenAvailabilityIcon />
												: series.availability === 'Yellow' ? <YellowAvailabilityIcon />
													: <RedAvailabilityIcon />
											}
										</SeriesAvailability>
										<SeriesData className='SeriesName'>
											{series.name}
										</SeriesData>
										<SeriesData className='SeriesRm'>
													RM: {series.relativeMaturity}
										</SeriesData>
										<div className='Spacer' style={{ flexGrow: 1 }} />
										{series.selectable && <SeriesAction className='SeriesAction' onClick={() => onAddSeries(series)}>
											<PlusIcon width={12} height={12} fill={theme.colors.darkGrey} />
										</SeriesAction>}
									</SeriesRow>
								))}
							</div>
						</ProductListSeries>
				}
			</ProductListContent>
		</ProductListContainer>
	);
}

const sidebarWidth = 70;
const containerOpenWidth = 320;
const containerClosedWidth = 20;

const ProductListContainer = styled.div`
	display: flex;
	flex-direction: column;
	width: ${containerClosedWidth}px;
	background-color: ${themeGet('colors.backgroundLM')};
	transition: min-width 0.3s linear, visibility 0s 0.3s linear;
	z-index: 99;
	height: 96%;
`;

const PullTabButton = styled.div`
	position: fixed;
	cursor: pointer;
	transition: left 0.3s linear, visibility 0s 0.3s linear;
`;

const ProductListContent = styled.div`
	display: flex;
	flex-direction: column;
	transition: visibility 0s 0.3s linear;
	flex: 1 1 auto;
	overflow: hidden;
`;

const ProductListHeader = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	align-items: center;
	margin: 28px 18px 12px 26px;
	padding: 12px;
	font-family: ${themeGet('fonts.heading')};
	font-size: ${themeGet('fontSizes.small')};
	font-weight: ${themeGet('fontWeights.bold')};
	border-top: 1px solid ${themeGet('colors.lightestGrey')};
	
	.infoIcon {
		margin-left: 4px;
		
		path, circle.fill {
			fill: ${themeGet('colors.darkGrey')};
			stroke: ${themeGet('colors.darkGrey')};
		}
	}

	circle {
		stroke: ${themeGet('colors.darkGrey')};
	}

	.inner {
		display: flex	
	}
`;

const ProductListSeries = styled.div`
	display: flex;
	flex-direction: column;
	overflow-y: auto;
	margin: 0px 10px 0px 30px;
	padding-right: 20px;
`;

const SeriesRow = styled.div`
	display: flex;
	flex-direction: row;
	width: 100%;
	height: 50px;
	min-height: 50px;
	background-color: ${themeGet('colors.white')};
	border: 1px solid ${themeGet('colors.lightestGrey')};
	border-radius: 5px;
	margin-bottom: 8px;
	align-items: center;
	box-shadow: 0px 2px 8px 0px rgba(0, 0, 0, 0.15);
`;

const SeriesData = styled.div`
	color: ${themeGet('colors.darkGrey')};
	font-family: ${themeGet('fonts.base')};
	font-size: ${themeGet('fontSizes.extraSmall')};
	font-weight: ${themeGet('fontWeights.bold')};
	padding: 0px 12px;
`;

const SeriesAction = styled.div`
	height: 100%;
    display: flex;
    align-items: center;
    padding-right: 12px;
    cursor: pointer;
`;

const AvailabilityLegend = styled.div`
	display: flex;
	flex-direction: row;
	padding-left: 10px;
	padding-right: 10px;
`;

const StyledSortArrows = styled(SortArrows) <{ sort: SeedSortDirection, rightspacing?: string, defaultarrowcolor?: string }>`
	justify-self: end;
	#upArrow {
		fill: ${props => props.sort === 'ascending' ? 
		themeGet('colors.primary') 
		: 
		props.defaultarrowcolor ? props.defaultarrowcolor : themeGet('colors.darkGrey')};
	}
	#downArrow {
		fill: ${props => props.sort === 'descending' ? 
		themeGet('colors.primary') 
		:
		props.defaultarrowcolor ? props.defaultarrowcolor : themeGet('colors.darkGrey')};
	}
	@media (hover: none) {
		right: ${props => props.rightspacing};
	}
`;
